import { DownOutlined, RightOutlined } from "@ant-design/icons";
import {
  XbotCell, XbotCellObject, XbotColumn, XbotNamedItem, XbotStatusPalette, XbotTableBlock, XbotTableConfig,
} from "@dto/pyxbot-agent.dto";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Button } from "antd";
import { CSSProperties, FC, Fragment, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ExportCollector, tableExport } from "../exportData";
import { XbotExportBar } from "../ExportBar";
import { clockParts, countText, isFiniteNumber, numberText } from "../format";
import { XbotBlockHost, XbotBlockProps, XbotOnOpenLink } from "../host";
import {
  canShowFromStart, firstRows, readNextRows, retryRows, RowPages, rowQuery, rowsFromStart, startOf, tableOffset, useReadWhile,
} from "../pages";
import { NgLink, pressable, StatusWord } from "../parts";
import {
  barColumnIndex, columnDecimals, columnHeadText, columnPalette, constantColumns, emptyText, fullTimeText, hoistedLine, isEmptyCell,
  isRanking, namedItems, namesHover, plainCellText, quietOkColumn, readAllRows, rowColumnIndexes, rowDetails, sameDayColumn,
  scheduleZone, SORT_ROWS_MAX, sortedOrder, stateLook, tableHasDetails, timeOf, typedCellWords, XbotDetailLine,
} from "../tableLook";

// Not XorMon's XmTable on purpose: an answer's table is small and lives inside a chat bubble, and it reads its rows a
// page at a time from the chat's stored result (data_ref) as the reader scrolls, which XmTable's own loading does not do.

const ROW_HEIGHT = 30;
/** About eleven rows show before the table scrolls inside the answer. */
const VIEW_HEIGHT = 330;
/** In the enlarged window the table takes the screen's height, less the window's head and foot. */
const WIDE_CHROME = 280;
/** The next page is asked for this many rows before the reader reaches the end of what is here. */
const READ_AHEAD = 10;

const TEXT_COLUMN: XbotColumn = { key: "", label: "", unit: null, unit_key: null, kind: "text" };
const NO_COLUMNS: XbotColumn[] = [];

// "Rows 21-40 of 80" for a later page, so it's never mistaken for the start; `drawn` caps it to the rows shown.
export function tableFootLine(pages: RowPages, drawn: number = pages.rows.length): string {
  const start = startOf(pages);
  const shown = Math.min(drawn, pages.rows.length);
  if (start > 0 && shown > 0) {
    return `Rows ${numberText(start + 1)}–${numberText(start + shown)} of ${numberText(pages.total)}`;
  }
  return shown < pages.total
    ? `Showing ${numberText(shown)} of ${countText(pages.total, "row", "rows")}`
    : countText(pages.total, "row", "rows");
}

// null when shown_first isn't a positive integer, or the result has no more rows than that (show all, as before).
export function shownFirstOf(config: XbotTableConfig, total: number): number | null {
  const first = config.shown_first;
  if (typeof first !== "number" || !Number.isInteger(first) || first < 1) {
    return null;
  }
  return total > first ? first : null;
}

export const EMPTY_TABLE_TEXT = "No rows were returned.";

/** What the table does with its columns once it has read their values (tableLook.ts). */
interface TableLook {
  /** Time columns within one day: the time alone, the date and time on hover. */
  timeOnly: ReadonlySet<number>;
  /** The column of percentages that draws a bar under its numbers. */
  bar: number | null;
  /** State columns where "ok" is drawn quietly. */
  quietOk: ReadonlySet<number>;
}

/** "10:10" for a time of a column within one day; the whole moment on hover. */
const TimeOnly: FC<{
  cell: XbotCell | undefined;
  column: XbotColumn;
  palette: XbotStatusPalette | null;
  timezone: string | null;
  onOpen?: XbotOnOpenLink;
}> = ({ cell, column, palette, timezone, onOpen }) => {
  const at = timeOf(cell);
  if (at === null) {
    return <TableCell cell={cell} column={column} palette={palette} timezone={timezone} onOpen={onOpen} />;
  }
  const parts = clockParts(at, timezone);
  // the hover says the moment whole, with its zone, as a full time cell does
  return <span title={`${parts.date} ${parts.minute}${timezone ? ` (${timezone})` : ""}`}>{parts.minute}</span>;
};

/** A thin bar under a percentage, 0-100. */
const CellBar: FC<{ cell: XbotCell | undefined }> = ({ cell }) => (isFiniteNumber(cell)
  ? <span className="xm-xbot-cell-bar" aria-hidden="true"><span style={{ width: `${Math.max(2, Math.min(100, cell))}%` }} /></span>
  : null);

/** No row in the whole result: neither a count above zero nor a row sent along. */
export function isEmptyTable(config: XbotTableConfig | null | undefined): boolean {
  const inline = Array.isArray(config?.rows_inline) ? config.rows_inline.length : 0;
  return config?.total_rows === 0 && inline === 0;
}

// Virtualized: only rows in view are ever mounted, so a table of thousands scrolls like one of ten.
// shown_first (table beside a multi-object chart) shows only its first rows until "Show all N rows" is pressed.
export const TableBlock: FC<XbotBlockProps<XbotTableBlock>> = ({ block, host }) => {
  const config = block.config;
  const { threadId, loadPage } = host;
  const [open, setOpen] = useState(!config.collapsed);
  // Copy and the files take every row of the result, not only the rows read so far.
  const collect = useCallback(
    (signal: AbortSignal) => tableExport(block, { threadId, loadPage }, { signal }),
    [block, threadId, loadPage],
  );
  const name = block.title || "table";
  if (isEmptyTable(config)) {
    // A heading row, "0 rows" and files of nothing say less than this one line.
    return <div className="xm-xbot-table-empty xm-xbot-muted">{EMPTY_TABLE_TEXT}</div>;
  }
  if (!open) {
    const total = typeof config.total_rows === "number" ? config.total_rows : 0;
    return <div className="xm-xbot-table-folded">
      <Button size="small" onClick={() => setOpen(true)}>{`Show ${countText(total, "row", "rows")}`}</Button>
      <XbotExportBar collect={collect} name={name} />
    </div>;
  }
  return <TableRows config={config} host={host} collect={collect} name={name} />;
};

const TableRows: FC<{ config: XbotTableConfig; host: XbotBlockHost; collect: ExportCollector; name: string }> = ({
  config, host, collect, name,
}) => {
  const { loadPage, threadId, onOpenLink } = host;
  const dataRef = config.data_ref;
  // one array while the config is the same, so the reading of the columns below is kept between renders
  const columns = Array.isArray(config.columns) ? config.columns : NO_COLUMNS;
  const timezone = config.window?.timezone ?? null;
  const [pages, setPages] = useState<RowPages>(() => firstRows(config));
  // The first rows only, until the person asks for all of them.
  const [folded, setFolded] = useState(() => shownFirstOf(config, firstRows(config).total) !== null);
  const firstShown = folded ? shownFirstOf(config, pages.total) : null;
  const shown = firstShown === null ? pages.rows.length : Math.min(firstShown, pages.rows.length);
  // The rows whose details are open, by their place among the rows read so far.
  const [opened, setOpened] = useState<ReadonlySet<number>>(() => new Set());
  // The enlarged table's sort by a heading click: the column, and highest first or not.
  const [sort, setSort] = useState<{ index: number; desc: boolean } | null>(null);
  // While the rest of the rows is read in for a sort: true, with the sort it waits for.
  const [readingAll, setReadingAll] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Stops reading every row for a sort once the table is gone.
  const aborter = useRef<AbortController | null>(null);
  useEffect(() => {
    const own = new AbortController();
    aborter.current = own;
    return () => own.abort();
  }, []);
  // The numbers stay once the whole result is shown, so the rows that were shown first can be found again.
  const numbered = tableOffset(config) > 0;
  const fromStart = canShowFromStart(config, pages, Boolean(loadPage));
  const showAll = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
    // The places now hold other rows, so no row stays open.
    setOpened(new Set());
    setPages(rowsFromStart(config));
  };
  const toggle = useCallback((index: number) => {
    setOpened((was) => {
      const next = new Set(was);
      if (!next.delete(index)) {
        next.add(index);
      }
      return next;
    });
  }, []);

  const canRead = firstShown === null && Boolean(loadPage) && rowQuery(pages, dataRef) !== null;
  // One extra row at the end says that more rows are on their way, or why they are not.
  const tail = firstShown === null && (canRead || pages.problem !== null);
  // Enlarged on a monitor, the table is as tall as the screen allows, so it is read with as little scrolling as
  // there can be; in the bubble it keeps its eleven rows.
  const viewHeight = host.wide && typeof window !== "undefined"
    ? Math.max(VIEW_HEIGHT, window.innerHeight - WIDE_CHROME)
    : VIEW_HEIGHT;
  const virtualizer = useVirtualizer({
    count: shown + (tail ? 1 : 0),
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 8,
    initialRect: { width: 640, height: viewHeight },
  });
  // An open row is its own line and its details; the scroll keeps its place when they open or close.
  const detailHeight = useCallback((index: number, height: number) => {
    virtualizer.resizeItem(index, ROW_HEIGHT + Math.max(0, height));
  }, [virtualizer]);
  const items = virtualizer.getVirtualItems();
  const lastInView = items.length ? items[items.length - 1].index : 0;
  const wantMore = canRead && lastInView + READ_AHEAD >= shown;

  useReadWhile(wantMore, pages, (signal) => {
    if (!loadPage) {
      return;
    }
    void readNextRows(pages, dataRef, threadId, loadPage, signal).then((next) => {
      if (!signal.aborted) {
        // Only if nothing else changed the rows meanwhile.
        setPages((was) => (was === pages ? next : was));
      }
    });
  });

  // A table that fits its box shows no scroll bar. Rows are rounded to
  // whole pixels, so a table exactly as tall as the box was a pixel taller and scrolled by one pixel.
  const fits = virtualizer.getTotalSize() + ROW_HEIGHT <= viewHeight + 1;
  const padTop = items.length ? items[0].start : 0;
  const padBottom = items.length ? virtualizer.getTotalSize() - items[items.length - 1].end : 0;
  // The table reads the rows it holds: one day's times say the time alone, a numeric
  // column one count of decimals, percentages draw a bar, a ranking is numbered, "ok" is quiet where it is the rule.
  // Each reading walks every row, so it is kept until the rows change, not redone as the reader scrolls (the
  // React Compiler leaves this component alone because of useVirtualizer, so nothing else keeps it).
  const wide = Boolean(host.wide);
  const { hoisted, hidden, heads, drawnColumns, look } = useMemo(() => {
    const allHeads = rowColumnIndexes(columns, columns.length);
    // In the bubble a column with the same words in every row is said once above the table.
    const hoisted = wide ? [] : constantColumns(columns, pages.rows, pages.total, allHeads);
    const hidden: ReadonlySet<number> = new Set(hoisted.map((one) => one.index));
    const heads = allHeads.filter((index) => !hidden.has(index));
    const drawnColumns = columns.map((column, at) => {
      const decimals = columnDecimals(column, pages.rows, at);
      return decimals === null ? column : { ...column, precision: decimals };
    });
    const look: TableLook = {
      timeOnly: new Set(heads.filter((at) => sameDayColumn(columns[at], pages.rows, at, timezone))),
      bar: barColumnIndex(columns, pages.rows, heads),
      quietOk: new Set(heads.filter((at) => quietOkColumn(columns[at], pages.rows, at))),
    };
    return { hoisted, hidden, heads, drawnColumns, look };
  }, [columns, pages.rows, pages.total, wide, timezone]);
  const detailed = tableHasDetails(columns);
  const whole = pages.rows.length >= pages.total && startOf(pages) === 0;
  // The enlarged table sorts by a heading click once it holds every row; the rest is read in on the first click
  // when the result is small enough, so a sort never ranks only the rows that came with the answer.
  const canReadAll = Boolean(loadPage) && config.data_ref?.state === "present" && pages.total <= SORT_ROWS_MAX;
  const sortable = Boolean(host.wide) && pages.total > 1 && (whole || canReadAll);
  const sortHint = host.wide && !sortable && pages.total > SORT_ROWS_MAX
    ? `Sorting needs all rows; this table has ${numberText(pages.total)}.`
    : undefined;
  const sorted = sortable && whole && sort !== null;
  // sorted once per sort and per rows, not on every scroll
  const sortIndex = sorted ? sort.index : null;
  const sortDesc = sorted ? sort.desc : false;
  const order = useMemo(
    () => (sortIndex === null ? null : sortedOrder(pages.rows, sortIndex, sortDesc)),
    [pages.rows, sortIndex, sortDesc],
  );
  const ranked = isRanking(config.order) && !sorted;
  const shownNumbers = numbered || ranked;
  const nextSort = (was: { index: number; desc: boolean } | null, index: number) => (was?.index === index
    ? (was.desc ? { index, desc: false } : null)
    : { index, desc: columns[index]?.kind === "number" || columns[index]?.kind === "time" });
  const onHead = (index: number) => {
    if (whole) {
      setSort((was) => nextSort(was, index));
      return;
    }
    const signal = aborter.current?.signal;
    if (readingAll || !loadPage || !signal || signal.aborted) {
      return;
    }
    setReadingAll(true);
    setFolded(false);
    void readAllRows(config, pages, threadId, loadPage, signal).then((all) => {
      if (signal.aborted) {
        return;
      }
      setReadingAll(false);
      setPages(all);
      if (all.rows.length >= all.total) {
        setSort(nextSort(null, index));
      }
    });
  };
  const width = Math.max(heads.length + (shownNumbers ? 1 : 0) + (detailed ? 1 : 0), 1);
  const firstNumber = startOf(pages) + 1;
  const footLine = tableFootLine(pages, shown);

  return <div className="xm-xbot-table">
    <div
      ref={scrollRef}
      className="xm-xbot-table-scroll"
      style={fits ? { maxHeight: viewHeight, overflowY: "hidden" } : { maxHeight: viewHeight }}
    >
      <table className="xm-xbot-table-grid">
        {heads.length ? <thead>
          <tr>
            {detailed ? <th scope="col" className="xm-xbot-cell-open" aria-label="Details" style={TOGGLE_CELL_STYLE} /> : null}
            {shownNumbers ? <th scope="col" className="xm-xbot-cell-rownum" title="Row of the whole result">#</th> : null}
            {heads.map((index) => (
              <th
                key={`${columns[index].key}-${index}`}
                scope="col"
                className={sortable ? `xm-xbot-cell-${columns[index].kind} is-sortable` : `xm-xbot-cell-${columns[index].kind}`}
                aria-sort={sort?.index === index ? (sort.desc ? "descending" : "ascending") : undefined}
                onClick={sortable ? () => onHead(index) : undefined}
                {...pressable(sortable ? () => onHead(index) : undefined)}
                title={sortable ? (whole ? "Sort by this column" : "Read all rows and sort by this column") : sortHint}
              >
                {columnHeadText(columns[index])}
                {sortable && sort?.index === index ? <span aria-hidden="true">{sort.desc ? " ↓" : " ↑"}</span> : null}
              </th>
            ))}
          </tr>
        </thead> : null}
        <tbody>
          {padTop > 0 ? <tr aria-hidden="true" style={{ height: padTop }} /> : null}
          {items.map((item) => (item.index < shown
            ? <TableRow
              key={item.index}
              index={item.index}
              number={shownNumbers ? firstNumber + item.index : null}
              cells={pages.rows[order ? order[item.index] : item.index]}
              columns={drawnColumns}
              look={look}
              palette={config.status_palette}
              timezone={timezone}
              onOpen={onOpenLink}
              hidden={hidden}
              detailed={detailed}
              open={opened.has(item.index)}
              onToggle={toggle}
              onDetailHeight={detailHeight}
              width={width}
            />
            : <tr key="tail" className="xm-xbot-table-tail">
              <td colSpan={width}>
                <span>{pages.problem ?? "Reading more rows…"}</span>
                {pages.canRetry ? <Button type="link" size="small" onClick={() => setPages(retryRows)}>Try again</Button> : null}
              </td>
            </tr>))}
          {padBottom > 0 ? <tr aria-hidden="true" style={{ height: padBottom }} /> : null}
        </tbody>
      </table>
    </div>
    <div className="xm-xbot-table-foot">
      <span className="xm-xbot-table-said xm-xbot-muted">
        <span title={ranked && config.order ? `Order: ${config.order}` : undefined}>{readingAll ? "Reading all rows…" : footLine}</span>
        {/* a numbered ranking already shows its order; the words stay on the count's hover. Sorted by a heading
            click, the foot says that order instead of the one the answer came in. */}
        {sorted && sort
          ? <span>{`Sorted by ${columnHeadText(columns[sort.index])}, ${sort.desc ? "highest" : "lowest"} first`}</span>
          : config.order && !ranked ? <span>{`Order: ${config.order}`}</span> : null}
      </span>
      {firstShown !== null || fromStart
        ? <Button size="small" className="xm-xbot-table-all" onClick={firstShown !== null ? () => setFolded(false) : showAll}>
          {`Show all ${countText(pages.total, "row", "rows")}`}
        </Button>
        : null}
      <XbotExportBar collect={collect} name={name} />
    </div>
    {/* what every row shares is a note under the data, with the block's other notes */}
    {hoisted.length ? <div className="xm-xbot-table-shared xm-xbot-muted">{hoistedLine(hoisted)}</div> : null}
  </div>;
};

const TableRow: FC<{
  /** The row's place among the rows read so far. */
  index: number;
  /** The row's place in the whole result, counted from 1; null when the table does not number its rows. */
  number: number | null;
  cells: XbotCell[];
  columns: XbotColumn[];
  palette: XbotStatusPalette | null;
  timezone: string | null;
  onOpen?: XbotOnOpenLink;
  /** Columns said once above the table, not in every row. */
  hidden: ReadonlySet<number>;
  /** The reading of the columns: one day's times, the bar, the quiet "ok". */
  look: TableLook;
  /** True when the table's rows can open to details: the row then starts with the cell that opens them. */
  detailed: boolean;
  open: boolean;
  onToggle: (index: number) => void;
  onDetailHeight: (index: number, height: number) => void;
  width: number;
}> = ({ index, number, cells, columns, palette, timezone, onOpen, hidden, look, detailed, open, onToggle, onDetailHeight, width }) => {
  const row = Array.isArray(cells) ? cells : [];
  const slots = rowColumnIndexes(columns, Math.max(columns.length, row.length)).filter((slot) => !hidden.has(slot));
  const details = detailed ? rowDetails(row, columns) : [];
  const showDetails = open && details.length > 0;
  return <Fragment>
    <tr>
      {detailed
        ? <td className="xm-xbot-cell-open" style={TOGGLE_CELL_STYLE}>
          {details.length ? <DetailsToggle open={showDetails} onToggle={() => onToggle(index)} /> : null}
        </td>
        : null}
      {number !== null ? <td className="xm-xbot-cell-rownum">{numberText(number)}</td> : null}
      {slots.map((slot) => {
        const column = columns[slot] ?? TEXT_COLUMN;
        const quiet = look.quietOk.has(slot) ? " is-quiet-ok" : "";
        return <td key={slot} className={`xm-xbot-cell-${column.kind}${quiet}`}>
          {look.timeOnly.has(slot)
            ? <TimeOnly cell={row[slot]} timezone={timezone} column={column} palette={palette} onOpen={onOpen} />
            : <TableCell cell={row[slot]} column={column} palette={palette} timezone={timezone} onOpen={onOpen} />}
          {look.bar === slot ? <CellBar cell={row[slot]} /> : null}
        </td>;
      })}
    </tr>
    {showDetails
      ? <DetailRow
        index={index}
        lines={details}
        palette={palette}
        timezone={timezone}
        onOpen={onOpen}
        onHeight={onDetailHeight}
        width={width}
      />
      : null}
  </Fragment>;
};

/** The line under an open row with its details; it tells the table how tall it is while it stands there. */
const DetailRow: FC<{
  index: number;
  lines: XbotDetailLine[];
  palette: XbotStatusPalette | null;
  timezone: string | null;
  onOpen?: XbotOnOpenLink;
  onHeight: (index: number, height: number) => void;
  width: number;
}> = ({ index, lines, palette, timezone, onOpen, onHeight, width }) => {
  const measure = useCallback((node: HTMLTableRowElement | null) => {
    if (!node) {
      return undefined;
    }
    onHeight(index, node.getBoundingClientRect().height);
    return () => onHeight(index, 0);
  }, [index, onHeight]);
  return <tr ref={measure} className="xm-xbot-table-detail">
    <td colSpan={width} style={DETAIL_CELL_STYLE}>
      <RowDetails lines={lines} palette={palette} timezone={timezone} onOpen={onOpen} />
    </td>
  </tr>;
};

// The cells: only layout here; colours come from the stylesheet's tokens, so dark theme follows automatically.

interface XbotCellContext {
  column: XbotColumn;
  /** The block's palette, for a column that names none of its own. */
  palette: XbotStatusPalette | null;
  /** The answer's time zone, for a time the cell does not zone itself. */
  timezone: string | null;
  onOpen?: XbotOnOpenLink;
}

// A switch that's off or paused is drawn muted, without a colour; `label` overrides the shown text.
export const CellState: FC<{ word: string; label?: string; palette: XbotStatusPalette | null }> = ({ word, label, palette }) => {
  const look = stateLook(word, palette);
  const text = label || word;
  if (look.muted) {
    return <span className="xm-xbot-status xm-xbot-muted">{text}</span>;
  }
  return <StatusWord word={look.word} palette={look.palette} label={text} />;
};

/** An empty cell: the words its column gives an empty value ("No record"), else the table's dash. */
const EmptyCell: FC<{ column: XbotColumn }> = ({ column }) => {
  const said = emptyText(column);
  return said
    ? <span className="xm-xbot-muted">{said}</span>
    : <span className="xm-xbot-muted" title="No value">–</span>;
};

// Words are the whole of what's shown (a schedule already says its own zone); cell.timezone is only for code/export.
// In a row's details a time is said in full, with its date and zone.
const TypedCell: FC<XbotCellContext & { cell: XbotCellObject; full?: boolean }> = ({
  cell, column, palette, timezone, onOpen, full,
}) => {
  const zone = typeof cell.timezone === "string" && cell.timezone.trim() ? cell.timezone : timezone;
  const when = typeof cell.time === "number" ? fullTimeText(cell.time, zone) : "";
  const words = full && when ? when : typedCellWords(cell) || when;
  const body: ReactNode = cell.status
    ? <CellState word={cell.status} label={words} palette={columnPalette(column, palette)} />
    : words;
  // a schedule is read whole on hover too, since a narrow column cuts it
  const hover = (full ? undefined : when) || namesHover(cell) || (scheduleZone(cell, column) ? words : undefined);
  return <span title={hover || undefined}>
    {cell.link ? <NgLink path={cell.link} onOpen={onOpen}>{body}</NgLink> : body}
  </span>;
};

// Only the words are drawn, never NG's raw value (kept for the JSON export); a number keeps its exact value on hover.
export const TableCell: FC<XbotCellContext & { cell: XbotCell | undefined; full?: boolean }> = ({
  cell, column, palette, timezone, onOpen, full,
}) => {
  if (isEmptyCell(cell)) {
    return <EmptyCell column={column} />;
  }
  if (typeof cell === "object" && cell !== null) {
    return <TypedCell cell={cell} column={column} palette={palette} timezone={timezone} onOpen={onOpen} full={full} />;
  }
  const value = cell as string | number | boolean;
  if (column.kind === "status" && typeof value === "string") {
    return <CellState word={value} palette={columnPalette(column, palette)} />;
  }
  const exact = typeof value === "number" && column.kind !== "time" ? String(value) : undefined;
  return <span title={exact}>{plainCellText(value, column, timezone)}</span>;
};

const LIST_STYLE: CSSProperties = { margin: 0, padding: 0, listStyle: "none" };

const NamedItemList: FC<{ items: XbotNamedItem[]; onOpen?: XbotOnOpenLink }> = ({ items, onOpen }) => (
  <ul className="xm-xbot-cell-items" style={LIST_STYLE}>
    {items.map((item, index) => {
      const note = typeof item.note === "string" ? item.note.trim() : "";
      return <li key={index}>
        <NgLink path={item.link} onOpen={onOpen}>{item.text.trim()}</NgLink>
        {note ? <span className="xm-xbot-muted">{` · ${note}`}</span> : null}
      </li>;
    })}
  </ul>
);

const DETAILS_STYLE: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "max-content minmax(0, 1fr)",
  gap: "2px 12px",
  margin: 0,
};
const DETAIL_VALUE_STYLE: CSSProperties = { margin: 0, overflowWrap: "anywhere" };

/** The details of a row: "Head: value" for every line, a list of names item by item. */
export const RowDetails: FC<{
  lines: XbotDetailLine[];
  palette: XbotStatusPalette | null;
  timezone: string | null;
  onOpen?: XbotOnOpenLink;
}> = ({ lines, palette, timezone, onOpen }) => (
  <dl className="xm-xbot-table-details" style={DETAILS_STYLE}>
    {lines.map((line) => <Fragment key={line.index}>
      <dt className="xm-xbot-muted">{columnHeadText(line.column)}</dt>
      <dd style={DETAIL_VALUE_STYLE}>
        {line.list
          ? <NamedItemList items={namedItems(line.cell)} onOpen={onOpen} />
          : <TableCell cell={line.cell} column={line.column} palette={palette} timezone={timezone} onOpen={onOpen} full />}
      </dd>
    </Fragment>)}
  </dl>
);

/** The cell of a table row's own details wide; the table's cells are one line high, these are not. */
const DETAIL_CELL_STYLE: CSSProperties = {
  height: "auto",
  maxWidth: "none",
  whiteSpace: "normal",
  padding: "4px 8px 8px 40px",
};

const TOGGLE_CELL_STYLE: CSSProperties = { width: "1%", padding: "0 0 0 4px" };

const DetailsToggle: FC<{ open: boolean; onToggle: () => void }> = ({ open, onToggle }) => {
  const what = open ? "Hide the details of this row" : "Show the details of this row";
  return <Button
    type="text"
    size="small"
    icon={open ? <DownOutlined /> : <RightOutlined />}
    aria-expanded={open}
    aria-label={what}
    title={what}
    onClick={onToggle}
  />;
};
